import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { tap } from 'rxjs/operators';
import { UtilsService } from 'src/app/shared/utils.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { StorageService } from 'src/app/shared/storage.service';
import { UsersFilterComponent } from './users-filter/users-filter.component';
import { UsersBlockDialogComponent } from './users-block-dialog/users-block-dialog.component';
import {
  VERIFICATION_DISPLAYS,
  VerificationDisplay,
  VerificationDisplayKey,
  resolveVerificationDisplay, VERIFICATION_LEGEND_ORDER
} from './users-verification-statuses';
import { InfoDialogData } from '../../shared/info-dialog/info-dialog.interface';
import { InfoDialogComponent } from '../../shared/info-dialog/info-dialog.component';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UsersComponent implements OnInit {
  displayedColumns: string[] = [
    'select',
    'id',
    'icons',
    'contact_person',
    'email',
    'mobile',
    'employer',
    'is_sitesoft_admin',
    'date_created',
    // 'date_created_UTC',
    'verification',
    'actions'
  ];

  dataSource = new UsersDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // Persistent list filters.
  private readonly FILTER_STORAGE_KEY = 'admin_users_filters';
  filterUserType: string = '';
  filterVerificationStatus: string = '';
  // '' is all users, '1' is blocked only, '0' is not blocked only.
  filterBlocked: string = '';
  filterAssociatedAccounts: { id: number; name: string }[] = [];
  filterEmployerAccounts: { id: number; name: string }[] = [];

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private storage: StorageService
  ) {}

  ngOnInit() {
    // Restore any persisted filters, apply them to the data source, then load.
    this.loadFilters();
    this.dataSource.filters = this.buildFilterParams();
    this.dataSource.getData(true);
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'date_created_UTC') {
        this.dataSource.order_by = 'date_created';
      } else {
        this.dataSource.order_by = this.sort.active;
      }

      this.dataSource.getData();
    });

    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  composeNewMessage(userId: number = null) {
    if (this.dataSource.selection.selected.length == 0 && !userId) {
      this.utils.showToast('You need to select some users first.');
      return; // early termination
    }

    this.utils.showModal(
      'Sending Messages',
      'You are about to send the message on behalf of ' +
        this.app.account.name +
        '. Switch to a different account if this is not intended.'
    );

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectUserIds: userId
            ? [userId]
            : this.dataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }

  onUnlockUser(userId: number = null) {
    if (!(userId > 0)) {
      return this.utils.showModal('Error', 'User ID is incorrect.');
    }
    this.api
      .makeRequest('post', `v2/admin/users/${userId}/unlock`)
      .then((res) => {
        this.utils.showModal(
          'User unlocked successfully',
          'The user can now login again.'
        );
      });
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  /**
   * Explains the red "Sign-in blocked" line under a status label.
   */
  readonly verificationBlockedTooltip: string =
    'The verification deadline has passed, so this user cannot sign in until they verify their email address. Use Resend Verification in the row menu to send a new link.';

  /**
   * The display (label, icon, colour) for a user row. Table, filter and legend share one definition.
   */
  verificationDisplay(user: any): VerificationDisplay {
    return VERIFICATION_DISPLAYS[resolveVerificationDisplay(user)];
  }

  /**
   * Whether the user is currently blocked from signing in on the basis of email verification.
   *
   * Derived from the display: the expired and reverification_pending statuses are exactly "deadline
   * passed" in the API (User::verificationStatus), and support users are never blocked.
   */
  verificationBlocked(user: any): boolean {
    return this.verificationDisplay(user).blocksSignIn;
  }

  /**
   * The hover tooltip describing a user's verification display, its deadline, and whether the user
   * can currently sign in.
   *
   * The deadline date is formatted dd/mm/yyyy. If a blocked status has no deadline the date phrase is
   * dropped rather than printing an invalid date.
   */
  verificationTooltip(user: any): string {
    const display = this.verificationDisplay(user);
    const dueAt = user.email_verification_due_at;
    const date = dueAt
      ? new Date(dueAt * 1000).toLocaleDateString('en-GB')
      : '';
    const onDate = date ? ' on ' + date : '';

    switch (display.key) {
      case 'exempt': {
        const underlying =
          VERIFICATION_DISPLAYS[user.verification_status as VerificationDisplayKey];
        return (
          'This is a ' +
          this.utils.getAppName() +
          ' support user. Support users are exempt from email verification and are never blocked from signing in.' +
          (underlying && user.verification_status !== 'exempt'
            ? ' Underlying status: ' + underlying.label + '.'
            : '')
        );
      }
      case 'verified':
        return (
          'Email verified. ' +
          (date
            ? 'The next verification is due on ' + date + '. '
            : '') +
          'Sign-in is not blocked.'
        );
      case 'unverified':
        return date
          ? 'Not verified yet. This user can still sign in until ' +
              date +
              '. After that, sign-in is blocked until they verify.'
          : 'Not verified yet and no verification deadline has been set, so sign-in is not blocked.';
      case 'reverification_pending':
        return (
          'The verification deadline passed' +
          onDate +
          ' and a reminder was sent. Sign-in is blocked until this user verifies.'
        );
      case 'expired':
        return (
          'The verification deadline passed' +
          onDate +
          '. Sign-in is blocked until this user verifies.'
        );
      default:
        return (
          'This user has never verified their email address and the deadline passed' +
          onDate +
          '. Sign-in is blocked until they verify.'
        );
    }
  }

  /**
   * Open the information dialog explaining every email verification status.
   *
   * @return {void}
   */
  onOpenVerificationLegend(): void {

    // Replace the {appName} token with the white-label application name.
    const appName = this.utils.getAppName();

    const appText = (text: string): string =>
      text.split('{appName}').join(appName);

    const data: InfoDialogData = {
      title: 'Email Verification Statuses',

      paragraphs: [
        'The status is calculated from each user\'s verification deadline.',
        'Only a passed deadline blocks sign-in. Being unverified before the deadline does not.',
        'Verification is renewed every 12 months. Reminder emails are sent 7 days and 1 day before the deadline.',
        'Re-verification pending is shown for the first 30 days after a missed deadline when a reminder had been sent. After that it becomes Expired. Both block sign-in the same way.'
      ],

      introNote: 'The 12 months, 7 days, 1 day and 30 days above are the current defaults and can be changed in the server configuration.',

      items: VERIFICATION_LEGEND_ORDER.map((key) => {

        const display = VERIFICATION_DISPLAYS[key];

        return {
          id: key,
          icon: display.icon,

          // Preserve existing Material icon colours.
          color: display.color ?? undefined,

          // Neutral colour for statuses without a Material theme colour.
          iconClass: !display.color ? 'info-icon-neutral' : undefined,

          label: display.label,

          subLabel: display.blocksSignIn
            ? 'Sign-in blocked'
            : undefined,

          subLabelClass: display.blocksSignIn
            ? 'text-danger'
            : undefined,

          details: [
            {
              label: 'When it appears',
              value: appText(display.legend.appears)
            },
            {
              label: 'Sign-in',
              value: appText(display.legend.signIn)
            },
            {
              label: 'Who it applies to',
              value: appText(display.legend.appliesTo)
            },
            {
              label: 'How it clears',
              value: appText(display.legend.clears)
            }
          ]
        };

      })
    };

    this.utils.showComponentDialog(
      InfoDialogComponent,
      data,
      {
        width: '760px'
      }
    );

  }

  /**
   * Resend an email verification link to a user (super admin support action).
   */
  onResendVerification(user: any) {
    this.api
      .makeRequest('post', `v2/admin/users/${user.id}/resend-verification`)
      .then((response) => {
        this.utils.showToast(
          response && response.message
            ? response.message
            : 'A verification link has been sent.'
        );
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Whether the Block user action is offered for a row (SC-3089). Not offered for a user who is already
   * blocked, for support (super admin) users, or for the signed in super admin. The API enforces the
   * same rules.
   */
  canBlockUser(user: any): boolean {
    return (
      !user.is_blocked &&
      !user.is_sitesoft_admin &&
      Number(user.id) !== Number(this.app.user?.id)
    );
  }

  /**
   * Plain text tooltip for the red Blocked label. A tooltip is text, not HTML, so interpolating the
   * reasons is safe.
   */
  blockedTooltip(user: any): string {
    let text = 'Blocked. Internal reason: ' + (user.blocked_reason || 'none recorded') + '.';
    if (user.blocked_client_note) {
      text += ' Shown to the client: ' + user.blocked_client_note + '.';
    }
    return text;
  }

  /**
   * Open the block dialog. The dialog collects the reasons and makes the request itself, and closes
   * with true on success so the list can refresh in place.
   */
  onBlockUser(user: any) {
    if (!this.canBlockUser(user)) {
      return;
    }
    this.utils
      .showComponentDialog(
        UsersBlockDialogComponent,
        {
          id: user.id,
          accounts_count: Number(user.accounts_count) || 0,
          is_partner: !!user.is_partner
        },
        { width: '640px' }
      )
      .then((blocked) => {
        if (blocked) {
          this.dataSource.getData();
        }
      });
  }

  /**
   * Unblock a user after a simple confirmation. The modal content is static HTML with no user
   * controlled text. The previous sessions stay revoked, so the user has to sign in again.
   */
  onUnblockUser(user: any) {
    if (!user.is_blocked) {
      return;
    }
    this.utils.showModal(
      'Unblock user',
      '<p>Unblock this user? They will be able to sign in again. Their previous sessions stay revoked, so they will need to sign in again.</p>',
      () => {
        this.api
          .makeRequest('post', `v2/admin/users/${user.id}/unblock`)
          .then((response) => {
            this.utils.showToast(
              response && response.message
                ? response.message
                : 'The user has been unblocked.'
            );
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Whether any list filter is currently active. Drives the Filter button's active styling.
   */
  get hasActiveFilters(): boolean {
    return (
      !!this.filterUserType ||
      !!this.filterVerificationStatus ||
      !!this.filterBlocked ||
      this.filterAssociatedAccounts.length > 0 ||
      this.filterEmployerAccounts.length > 0
    );
  }

  /**
   * Open the list filter dialog. The dialog edits a working copy of the filters and returns the
   * selection on Apply/Clear; this component persists the result and reloads the list.
   */
  onOpenFilters() {
    this.utils
      .showComponentDialog(
        UsersFilterComponent,
        {
          userType: this.filterUserType,
          verificationStatus: this.filterVerificationStatus,
          blocked: this.filterBlocked,
          associatedAccounts: this.filterAssociatedAccounts,
          employerAccounts: this.filterEmployerAccounts
        },
        { width: '400px' }
      )
      .then((response) => {
        if (!response) {
          // Dialog dismissed without applying or clearing: leave filters untouched.
          return;
        }
        this.filterUserType = response.userType || '';
        this.filterVerificationStatus = response.verificationStatus || '';
        this.filterBlocked = response.blocked || '';
        this.filterAssociatedAccounts = Array.isArray(
          response.associatedAccounts
        )
          ? response.associatedAccounts
          : [];
        this.filterEmployerAccounts = Array.isArray(response.employerAccounts)
          ? response.employerAccounts
          : [];
        this.applyFilters();
      });
  }

  /**
   * Build the query params for the current filter selection.
   */
  private buildFilterParams(): any {
    const params: any = {};
    if (this.filterUserType) {
      params.user_type = this.filterUserType;
    }
    if (this.filterVerificationStatus) {
      params.verification_status = this.filterVerificationStatus;
    }
    if (this.filterBlocked) {
      params.blocked = this.filterBlocked;
    }
    if (this.filterAssociatedAccounts.length) {
      params.associated_account_ids = this.filterAssociatedAccounts
        .map((a) => a.id)
        .join(',');
    }
    if (this.filterEmployerAccounts.length) {
      params.employer_account_ids = this.filterEmployerAccounts
        .map((a) => a.id)
        .join(',');
    }
    return params;
  }

  /**
   * Persist the current filter selection so it is restored on the next visit.
   */
  private saveFilters() {
    this.storage.set(this.FILTER_STORAGE_KEY, {
      userType: this.filterUserType,
      verificationStatus: this.filterVerificationStatus,
      blocked: this.filterBlocked,
      associatedAccounts: this.filterAssociatedAccounts,
      employerAccounts: this.filterEmployerAccounts
    });
  }

  /**
   * Restore the persisted filter selection (if any).
   */
  private loadFilters() {
    const stored = this.storage.get(this.FILTER_STORAGE_KEY);
    if (!stored) {
      return;
    }
    this.filterUserType = stored.userType || '';
    this.filterVerificationStatus = stored.verificationStatus || '';
    // Filters stored before the Blocked filter existed have no key, so default to all users.
    this.filterBlocked = stored.blocked || '';
    this.filterAssociatedAccounts = Array.isArray(stored.associatedAccounts)
      ? stored.associatedAccounts
      : [];
    this.filterEmployerAccounts = Array.isArray(stored.employerAccounts)
      ? stored.employerAccounts
      : [];
  }

  /**
   * Apply the current filters, persist them, and reload the list.
   */
  applyFilters() {
    this.saveFilters();
    this.dataSource.filters = this.buildFilterParams();
    this.dataSource.getData(true);
  }
}

export class UsersDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';

  // Active list filters, merged into every data request so pagination and sorting preserve them.
  filters: any = {};

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/admin/users', resetOffset, this.filters);
  }
}
